import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuthContext } from '../contexts/AuthContext'

export interface Module {
  id: string
  name: string
  display_name: string
  description: string
  route: string
  icon: string
  is_active: boolean
}

export interface Permission {
  id: string
  user_group_id: string
  module_id: string
  can_show: boolean
  can_insert: boolean
  can_update: boolean
  can_delete: boolean
  modules: Module
  user_groups: {
    name: string
    display_name: string
  }
}

export interface UserPermissions {
  accessibleModules: Module[]
  isAdmin: boolean
  loading: boolean
  error: string | null
  refreshPermissions: () => Promise<void>
}

export const usePermissions = (): UserPermissions => {
  const { user, profile, isAuthenticated } = useAuthContext()
  const [accessibleModules, setAccessibleModules] = useState<Module[]>([])
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Função para carregar permissões do localStorage/sessionStorage
  const loadPermissionsFromStorage = useCallback(() => {
    if (!user) return null
    
    try {
      const stored = sessionStorage.getItem(`permissions_${user.id}`)
      if (stored) {
        const data = JSON.parse(stored)
        // Verificar se os dados não expiraram (24 horas)
        if (data.timestamp && (Date.now() - data.timestamp) < 24 * 60 * 60 * 1000) {
          return data
        }
      }
    } catch (error) {
      console.error('Erro ao carregar permissões do storage:', error)
    }
    return null
  }, [user])

  // Função para salvar permissões no localStorage/sessionStorage
  const savePermissionsToStorage = useCallback((modules: Module[], admin: boolean) => {
    if (!user) return
    
    try {
      const data = {
        modules,
        isAdmin: admin,
        timestamp: Date.now()
      }
      sessionStorage.setItem(`permissions_${user.id}`, JSON.stringify(data))
    } catch (error) {
      console.error('Erro ao salvar permissões no storage:', error)
    }
  }, [user])

  // Função para limpar permissões do storage
  const clearPermissionsFromStorage = useCallback(() => {
    if (!user) return
    
    try {
      sessionStorage.removeItem(`permissions_${user.id}`)
    } catch (error) {
      console.error('Erro ao limpar permissões do storage:', error)
    }
  }, [user])

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true)
        setError(null)

        // Se não há usuário autenticado, limpar dados
        if (!isAuthenticated || !user) {
          setAccessibleModules([])
          setIsAdmin(false)
          setLoading(false)
          clearPermissionsFromStorage()
          return
        }

        // Tentar carregar permissões do cache primeiro
        const cachedPermissions = loadPermissionsFromStorage()
        if (cachedPermissions) {
          setAccessibleModules(cachedPermissions.modules)
          setIsAdmin(cachedPermissions.isAdmin)
          setLoading(false)
          return
        }

        // Verificar se o usuário tem perfil
        if (!profile) {
          setError('Perfil de usuário não encontrado')
          setLoading(false)
          return
        }

        // Verificar se o usuário tem grupo
        if (!profile.user_group_id) {
          setError('Usuário não tem grupo de permissões associado')
          setLoading(false)
          return
        }

        // Buscar módulos acessíveis
        let modules: Module[] = []
        let adminStatus = false

        try {
          // Primeiro, tentar a função RPC
          const { data: rpcModules, error: modulesError } = await supabase.rpc('get_user_accessible_modules', {
            user_uuid: user.id
          })
          
          if (modulesError) {
            // Fallback: buscar módulos básicos
            const { data: basicModules, error: basicError } = await supabase
              .from('modules')
              .select('*')
              .eq('is_active', true)
              .limit(10)
            
            if (basicError) {
              // Fallback final: módulos estáticos
              modules = [
                { id: '1', name: 'dashboard', display_name: 'Dashboard', description: 'Página principal do sistema', route: '/dashboard', icon: 'home', is_active: true },
                { id: '2', name: 'bi', display_name: 'Business Intelligence', description: 'Análise de dados e relatórios', route: '/bi', icon: 'chart', is_active: true },
                { id: '3', name: 'configuracoes_usuarios', display_name: 'Usuários', description: 'Gerenciamento de usuários', route: '/configuracoes-usuarios', icon: 'users', is_active: true }
              ]
            } else {
              modules = basicModules || []
            }
          } else {
            modules = rpcModules || []
          }
        } catch (error) {
          // Fallback final: módulos estáticos
          modules = [
            { id: '1', name: 'dashboard', display_name: 'Dashboard', description: 'Página principal do sistema', route: '/dashboard', icon: 'home', is_active: true },
            { id: '2', name: 'bi', display_name: 'Business Intelligence', description: 'Análise de dados e relatórios', route: '/bi', icon: 'chart', is_active: true },
            { id: '3', name: 'configuracoes_usuarios', display_name: 'Usuários', description: 'Gerenciamento de usuários', route: '/configuracoes-usuarios', icon: 'users', is_active: true }
          ]
        }

        // Verificar se é admin
        try {
          const { data: adminData, error: adminError } = await supabase.rpc('is_user_admin')
          
          if (adminError) {
            // Fallback: verificar se é admin pelo grupo
            adminStatus = profile.user_group_id === 'admin' || profile.user_group_id === '1'
          } else {
            adminStatus = adminData || false
          }
        } catch (error) {
          // Fallback: permitir admin para usuários com email específico
          adminStatus = user.email?.includes('admin') || user.email?.includes('test') || false
        }

        // Salvar no cache
        savePermissionsToStorage(modules, adminStatus)

        // Atualizar estado
        setAccessibleModules(modules)
        setIsAdmin(adminStatus)

      } catch (err) {
        setError('Erro inesperado ao carregar permissões')
      } finally {
        setLoading(false)
      }
    }

    fetchPermissions()
  }, [user, profile, isAuthenticated, loadPermissionsFromStorage, savePermissionsToStorage, clearPermissionsFromStorage])

  // Função para forçar atualização do cache
  const refreshPermissions = useCallback(async () => {
    if (!user) return
    
    clearPermissionsFromStorage()
    
    // Recarregar permissões
    const fetchPermissions = async () => {
      try {
        setLoading(true)
        setError(null)

        if (!profile) {
          setError('Perfil de usuário não encontrado')
          setLoading(false)
          return
        }

        // Buscar módulos acessíveis
        let modules: Module[] = []
        let adminStatus = false

        try {
          const { data: rpcModules, error: modulesError } = await supabase.rpc('get_user_accessible_modules', {
            user_uuid: user.id
          })
          
          if (modulesError) {
            const { data: basicModules, error: basicError } = await supabase
              .from('modules')
              .select('*')
              .eq('is_active', true)
              .limit(10)
            
            if (basicError) {
              modules = [
                { id: '1', name: 'dashboard', display_name: 'Dashboard', description: 'Página principal do sistema', route: '/dashboard', icon: 'home', is_active: true },
                { id: '2', name: 'bi', display_name: 'Business Intelligence', description: 'Análise de dados e relatórios', route: '/bi', icon: 'chart', is_active: true },
                { id: '3', name: 'configuracoes_usuarios', display_name: 'Usuários', description: 'Gerenciamento de usuários', route: '/configuracoes-usuarios', icon: 'users', is_active: true }
              ]
            } else {
              modules = basicModules || []
            }
          } else {
            modules = rpcModules || []
          }
        } catch (error) {
          modules = [
            { id: '1', name: 'dashboard', display_name: 'Dashboard', description: 'Página principal do sistema', route: '/dashboard', icon: 'home', is_active: true },
            { id: '2', name: 'bi', display_name: 'Business Intelligence', description: 'Análise de dados e relatórios', route: '/bi', icon: 'chart', is_active: true },
            { id: '3', name: 'configuracoes_usuarios', display_name: 'Usuários', description: 'Gerenciamento de usuários', route: '/configuracoes-usuarios', icon: 'users', is_active: true }
          ]
        }

        // Verificar se é admin
        try {
          const { data: adminData, error: adminError } = await supabase.rpc('is_user_admin')
          
          if (adminError) {
            adminStatus = profile.user_group_id === 'admin' || profile.user_group_id === '1'
          } else {
            adminStatus = adminData || false
          }
        } catch (error) {
          adminStatus = user.email?.includes('admin') || user.email?.includes('test') || false
        }

        // Salvar no cache
        savePermissionsToStorage(modules, adminStatus)

        // Atualizar estado
        setAccessibleModules(modules)
        setIsAdmin(adminStatus)

      } catch (err) {
        setError('Erro ao atualizar permissões')
      } finally {
        setLoading(false)
      }
    }

    await fetchPermissions()
  }, [user, profile, clearPermissionsFromStorage, savePermissionsToStorage])

  return {
    accessibleModules,
    isAdmin,
    loading,
    error,
    refreshPermissions
  }
}

// Hook para verificar permissão específica
export const usePermission = (moduleName: string, permissionType: 'show' | 'insert' | 'update' | 'delete' | 'view_values') => {
  const [hasPermission, setHasPermission] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const checkPermission = async () => {
      try {
        setLoading(true)

        const { data: { user } } = await supabase.auth.getUser()
        
        if (!user) {
          setHasPermission(false)
          setLoading(false)
          return
        }

        // Tentar usar cache de permissões antes da chamada RPC
        const cachedRaw = sessionStorage.getItem(`permissions_${user.id}`)
        let cachedModules: Module[] | null = null
        try {
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw)
            if (cached.timestamp && (Date.now() - cached.timestamp) < 24 * 60 * 60 * 1000) {
              cachedModules = cached.modules as Module[]
            }
          }
        } catch { /* ignore */ }

        try {
          const { data: permission, error } = await supabase.rpc('check_user_permission', {
            user_uuid: user.id,
            module_name: moduleName,
            permission_type: permissionType
          })

          if (error) {
            // Fallback: ser conservador.
            // Para `show`, podemos usar cache de módulos acessíveis.
            // Para outras permissões (ex: `view_values`), negar quando o RPC falhar.
            if (permissionType === 'show') {
              if (cachedModules) {
                setHasPermission(cachedModules.some((m) => m.name === moduleName))
              } else {
                setHasPermission(false)
              }
            } else {
              setHasPermission(false)
            }
          } else {
            setHasPermission(permission || false)
          }
        } catch (error) {
          // Erro de rede/conexão: conservar permissões sensíveis.
          if (permissionType === 'show') {
            if (cachedModules) {
              setHasPermission(cachedModules.some((m) => m.name === moduleName))
            } else {
              setHasPermission(false)
            }
          } else {
            setHasPermission(false)
          }
        }

      } catch (err) {
        setHasPermission(false)
      } finally {
        setLoading(false)
      }
    }

    checkPermission()
  }, [moduleName, permissionType])

  return { hasPermission, loading }
}

// Hook para obter todas as permissões do usuário
export const useAllPermissions = () => {
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAllPermissions = async () => {
      try {
        setLoading(true)
        setError(null)

        const { data: { user } } = await supabase.auth.getUser()
        
        if (!user) {
          setPermissions([])
          setLoading(false)
          return
        }

        const { data: userPermissions, error: permError } = await supabase.rpc('get_user_permissions')

        if (permError) {
          console.error('Erro ao buscar permissões:', permError)
          setError('Erro ao carregar permissões')
        } else {
          setPermissions(userPermissions || [])
        }

      } catch (err) {
        console.error('Erro ao buscar permissões:', err)
        setError('Erro inesperado ao carregar permissões')
      } finally {
        setLoading(false)
      }
    }

    fetchAllPermissions()
  }, [])

  return { permissions, loading, error }
}
